/**
 * Bottom sheet showing badge details, progress, and editorial texture
 */

import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Progress } from '@/components/ui/progress';
import { BadgeProgress } from '@/hooks/useBadges';
import { getTextureStyle } from '@/components/profile/BadgesCard';
import { format } from 'date-fns';

interface BadgeDetailSheetProps {
  badge: BadgeProgress | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BadgeDetailSheet({ badge, open, onOpenChange }: BadgeDetailSheetProps) {
  if (!badge) return null;

  const { badge: def, earned, earnedAt, currentValue, progress } = badge;
  const Icon = def.icon;
  const remaining = Math.max(0, def.threshold - currentValue);

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="pb-0">
          <DrawerTitle className="sr-only">{def.name}</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col items-center px-6 pb-8 pt-2">
          {/* Badge Icon */}
          <div
            className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg overflow-hidden relative"
            style={{
              background: earned
                ? `linear-gradient(135deg, ${def.gradientFrom}, ${def.gradientTo})`
                : undefined,
            }}
            {...(!earned && { className: 'w-20 h-20 rounded-full flex items-center justify-center shadow-lg bg-muted relative overflow-hidden' })}
          >
            {/* Texture overlay */}
            {earned && (
              <div
                className="absolute inset-0 rounded-full"
                style={{ backgroundImage: getTextureStyle(def.texture) }}
              />
            )}
            <Icon className={`w-10 h-10 relative z-10 ${earned ? 'text-white drop-shadow-sm' : 'text-muted-foreground/40'}`} />
          </div>

          {/* Name */}
          <h3 className="text-lg font-semibold text-foreground mt-4">{def.name}</h3>
          <p className="text-sm text-muted-foreground mt-1">{def.description}</p>

          {earned ? (
            <p className="text-xs text-muted-foreground mt-4">
              Earned {earnedAt ? format(new Date(earnedAt), 'MMM d, yyyy') : 'recently'}
            </p>
          ) : (
            <div className="w-full mt-6 space-y-3">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{currentValue} / {def.threshold}</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2.5" />
              {remaining > 0 && (
                <p className="text-sm text-muted-foreground text-center">
                  {remaining} more to go!
                </p>
              )}
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
